import React from 'react'
import moment from 'moment'
import { Box, Chip, Stack, Typography } from '@mui/material'
import DetailsBox from '../../../../components/Cards/DetailsBox'
import { DEBRIEF_FIELDS } from '../../ePCR/liveCapture/catalogue'
import {
  getAction,
  getCategory,
  getMergedTimeline,
} from '../../ePCR/liveCapture/engine'
import {
  describeLiveEvent,
  fmtOffset,
  fmtTime,
  hasLiveEvents,
  liveStartOf,
  toneOf,
} from '../../ePCR/liveCapture/liveUi'

// Read-only live timeline (arrest + critical care consoles), categories and
// hot debrief for the record viewer.
const LiveTimelineModule = ({ record }) => {
  const categories = record?.criticalCare?.categories ?? []
  const debrief = record?.criticalCare?.debrief
  if (!hasLiveEvents(record) && !categories.length && !debrief) return null

  const timeline = getMergedTimeline(record)
  const start = liveStartOf(record)
  const weight = record?.criticalCare?.weightKg

  return (
    <DetailsBox title="Live timeline">
      <Stack gap={1.5} width="100%">
        {(categories.length > 0 || weight) && (
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            {categories.map((key) => {
              const category = getCategory(key)
              const tone = toneOf(category?.tone)
              return (
                <Chip
                  key={key}
                  label={category?.label ?? key}
                  sx={{ bgcolor: tone.bg, color: tone.dark, fontWeight: 700 }}
                />
              )
            })}
            {weight && (
              <Chip
                variant="outlined"
                label={`Weight ${weight}kg${
                  record.criticalCare.weightSource
                    ? ` (${record.criticalCare.weightSource})`
                    : ''
                }`}
              />
            )}
          </Stack>
        )}
        {record?.cardiacArrest?.startedAt && (
          <Typography variant="body2" color="text.secondary">
            Arrest console started{' '}
            {moment(record.cardiacArrest.startedAt).format(
              'DD/MM/YYYY HH:mm:ss',
            )}
          </Typography>
        )}
        <Stack gap={0.25}>
          {timeline.map((event) => {
            const tone = toneOf(getAction(event.type)?.tone)
            return (
              <Stack
                key={event.id || `${event.type}-${event.time}`}
                direction="row"
                gap={1.5}
                sx={{
                  borderLeft: 4,
                  borderLeftColor: tone.main,
                  pl: 1,
                  py: 0.25,
                }}
              >
                <Typography
                  fontFamily="monospace"
                  fontWeight={700}
                  minWidth={68}
                >
                  {fmtTime(event.time)}
                </Typography>
                <Typography
                  fontFamily="monospace"
                  color="text.secondary"
                  minWidth={64}
                >
                  {fmtOffset(event.time, start)}
                </Typography>
                <Typography sx={{ overflowWrap: 'anywhere' }}>
                  {describeLiveEvent(event)}
                </Typography>
              </Stack>
            )
          })}
        </Stack>
        {debrief && (
          <Box>
            <Typography fontWeight={700} mb={0.5}>
              Hot debrief
              {debrief.completedAt
                ? ` (${moment(debrief.completedAt).format('DD/MM/YYYY HH:mm')})`
                : ''}
            </Typography>
            <Stack gap={0.5}>
              {DEBRIEF_FIELDS.filter((field) => debrief[field.key]).map(
                (field) => (
                  <Box key={field.key} display="flex" gap={0.5} flexWrap="wrap">
                    <Typography fontWeight="bold">{field.label}:</Typography>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                      {String(debrief[field.key])}
                    </Typography>
                  </Box>
                ),
              )}
            </Stack>
          </Box>
        )}
      </Stack>
    </DetailsBox>
  )
}

export default LiveTimelineModule
